import { createFetch } from '@vueuse/core'
import { defineStore } from 'pinia'

import router from '@/router'
import { useAppStore } from '@/stores/app'

/*
 * Exported because not everything that talks to stanic-api is a fetch: the map tiles
 * are <img> elements, and their addresses are built by hand in stores/mapSources.ts.
 * One constant rather than the same string written twice, which would drift.
 */
export const API_BASE_URL = '/api/stanic-api/v1'

/*
 * The operator's customer switcher, on every request: `customer_id` narrows every
 * fleet-wide read to that customer's fleet, and a record the operator creates while
 * narrowed (a place, a driver, a rule) is that customer's. Every request and not a list
 * of the fleet-wide ones, because a list is what goes stale — the API ignores the
 * parameter wherever it means nothing, and from anybody who is not the operator.
 */
const withCustomer = (url: string, customerId: string): string => {
    if (!customerId || /[?&]customer_id=/.test(url)) return url

    return `${url}${url.includes('?') ? '&' : '?'}customer_id=${encodeURIComponent(customerId)}`
}

export const useFetchStore = defineStore('fetch', () => {
    const appStore = useAppStore()

    const fetch = createFetch({
        baseUrl: API_BASE_URL,
        options: {
            /*
             * The service says why it refused — { error: { code, message } } — and
             * useFetch drops the body of a failed response unless told to keep it.
             * Without this every refusal would read "an error occurred" in the UI
             * while the reason sat unread in the response.
             */
            updateDataOnError: true,
            beforeFetch({ url, options }) {
                const headers = new Headers(options.headers)
                headers.set('Authorization', `Bearer ${appStore.getJWT()}`)
                options.headers = headers

                return { url: withCustomer(url, appStore.narrowedCustomer()), options }
            },
            async onFetchError(ctx) {
                if (ctx.response?.status === 401) {
                    appStore.setLogout()
                    await router.push({ name: 'login' })
                }

                return ctx
            },
        },
    })

    return { fetch }
})
